import { useState, useMemo } from 'react'
import { useAuth } from '@clerk/clerk-react'
import type { FixturePlayersOut, PlayerRow, TeamLite } from '../types'
import { PlayerList } from './PlayerList'
import { PlayerComparison } from './PlayerComparison'
import { InjuryList } from './InjuryList'
import { PlayerProfileModal } from './PlayerProfileModal'
import { usePlayerShortlist } from '../context/UserDataContext'

// Check if Clerk is configured
const clerkEnabled = !!(import.meta as any).env?.VITE_CLERK_PUBLISHABLE_KEY

interface PlayersTabProps {
  players: FixturePlayersOut | null
  competitionName?: string
  seasonLabel?: string
}

type WindowKey = 'season' | 'last10' | 'last5' | 'last3'
type StatMode = 'total' | 'per90'
type TeamFilter = 'both' | 'home' | 'away'

export function PlayersTab({ players, competitionName, seasonLabel }: PlayersTabProps) {
  const auth = clerkEnabled ? useAuth() : { isSignedIn: false }
  const { isSignedIn } = auth
  const { shortlist, isShortlisted } = usePlayerShortlist()

  const [window, setWindow] = useState<WindowKey>('last5')
  const [statMode, setStatMode] = useState<StatMode>('total')
  const [teamFilter, setTeamFilter] = useState<TeamFilter>('both')
  const [compareMode, setCompareMode] = useState(false)
  const [selectedPlayers, setSelectedPlayers] = useState<PlayerRow[]>([])
  const [showShortlistedOnly, setShowShortlistedOnly] = useState(false)
  const [showPredictedLineups, setShowPredictedLineups] = useState(false)
  const [selectedPlayerForModal, setSelectedPlayerForModal] = useState<{ player: PlayerRow; team: TeamLite } | null>(null)

  // All players combined for comparison selection
  const allPlayers = useMemo(() => {
    if (!players) return []
    return [
      ...players.predicted_xi_home,
      ...players.predicted_xi_away,
      ...players.bench_home,
      ...players.bench_away,
    ]
  }, [players])

  // Filter players by shortlist if enabled
  const filterByShortlist = (rows: PlayerRow[]) => {
    if (!showShortlistedOnly) return rows
    return rows.filter((p) => isShortlisted(p.player_id))
  }

  // Count shortlisted players in this fixture
  const shortlistedCount = useMemo(() => {
    if (!players) return 0
    return allPlayers.filter((p) => isShortlisted(p.player_id)).length
  }, [allPlayers, isShortlisted])

  const togglePlayerSelection = (player: PlayerRow) => {
    setSelectedPlayers((prev) => {
      const exists = prev.some((p) => p.player_id === player.player_id)
      if (exists) {
        return prev.filter((p) => p.player_id !== player.player_id)
      }
      if (prev.length >= 3) {
        return prev // Max 3 players
      }
      return [...prev, player]
    })
  }

  const isPlayerSelected = (playerId: number) =>
    selectedPlayers.some((p) => p.player_id === playerId)

  const handleExitCompareMode = () => {
    setCompareMode(false)
    setSelectedPlayers([])
  }

  const handleRemovePlayer = (playerId: number) => {
    setSelectedPlayers((prev) => prev.filter((p) => p.player_id !== playerId))
  }

  if (!players) {
    return <div className="small">No player data yet. Ingest completed fixtures first.</div>
  }

  const windowLabel = { season: 'Season', last10: 'L10', last5: 'L5', last3: 'L3' }

  return (
    <div className="players-tab-v2">
      {/* Section Header */}
      <div className="players-section-header">
        <h2 className="players-section-title">Player Shooting</h2>
        <div className="players-stat-mode-toggle">
          <button
            className={`stat-mode-btn ${statMode === 'total' ? 'active' : ''}`}
            onClick={() => setStatMode('total')}
          >
            Total
          </button>
          <button
            className={`stat-mode-btn ${statMode === 'per90' ? 'active' : ''}`}
            onClick={() => setStatMode('per90')}
          >
            Per 90
          </button>
        </div>
      </div>

      {/* League Info & Team Filter */}
      <div className="players-filter-row">
        <span className="players-league-info">
          Showing stats for {competitionName || 'League'} {seasonLabel || '25/26'}
        </span>
        <div className="players-team-filter">
          <button
            className={`team-filter-btn ${teamFilter === 'home' ? 'active' : ''}`}
            onClick={() => setTeamFilter(teamFilter === 'home' ? 'both' : 'home')}
          >
            {players.home_team.crest_url ? (
              <img src={players.home_team.crest_url} alt="" className="team-filter-logo" />
            ) : (
              <span className="team-filter-abbr">{players.home_team.name.substring(0, 3).toUpperCase()}</span>
            )}
          </button>
          <button
            className={`team-filter-btn-center ${teamFilter === 'both' ? 'active' : ''}`}
            onClick={() => setTeamFilter('both')}
          >
            Both
          </button>
          <button
            className={`team-filter-btn ${teamFilter === 'away' ? 'active' : ''}`}
            onClick={() => setTeamFilter(teamFilter === 'away' ? 'both' : 'away')}
          >
            {players.away_team.crest_url ? (
              <img src={players.away_team.crest_url} alt="" className="team-filter-logo" />
            ) : (
              <span className="team-filter-abbr">{players.away_team.name.substring(0, 3).toUpperCase()}</span>
            )}
          </button>
        </div>
      </div>

      {/* Predicted Lineups Button */}
      <button
        className="predicted-lineups-btn"
        onClick={() => setShowPredictedLineups(!showPredictedLineups)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        {players.confirmed ? 'Confirmed Lineups' : 'Predicted Lineups'}
      </button>

      {/* Time Window Filter */}
      <div className="players-window-row">
        <div className="players-window-chips">
          {(['season', 'last10', 'last5', 'last3'] as WindowKey[]).map((w) => (
            <button
              key={w}
              className={`window-chip ${window === w ? 'active' : ''}`}
              onClick={() => setWindow(w)}
            >
              {windowLabel[w]}
            </button>
          ))}
        </div>
        <div className="players-action-chips">
          <button
            className={`action-chip ${compareMode ? 'active' : ''}`}
            onClick={() => (compareMode ? handleExitCompareMode() : setCompareMode(true))}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10" />
              <line x1="12" y1="20" x2="12" y2="4" />
              <line x1="6" y1="20" x2="6" y2="14" />
            </svg>
            {compareMode ? 'Exit' : 'Compare'}
          </button>
          {isSignedIn && shortlist.length > 0 && (
            <button
              className={`action-chip ${showShortlistedOnly ? 'active' : ''}`}
              onClick={() => setShowShortlistedOnly(!showShortlistedOnly)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill={showShortlistedOnly ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              {shortlistedCount > 0 ? shortlistedCount : ''}
            </button>
          )}
        </div>
      </div>

      {/* Compare mode info */}
      {compareMode && selectedPlayers.length < 2 && (
        <div className="compare-info-v2">
          Select 2-3 players to compare
        </div>
      )}

      {/* Comparison panel */}
      {selectedPlayers.length >= 2 && (
        <PlayerComparison
          players={selectedPlayers}
          window={window}
          onClose={handleExitCompareMode}
          onRemovePlayer={handleRemovePlayer}
        />
      )}

      {/* Lineup confirmation status */}
      {players.confirmed && (
        <div className="lineup-confirmed-banner-v2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>Lineups confirmed</span>
          {players.lineup_confirmed_at && (
            <span className="lineup-time">
              {new Date(players.lineup_confirmed_at).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          )}
        </div>
      )}

      {/* Injury lists */}
      {(players.injuries_home?.length > 0 || players.injuries_away?.length > 0) && (
        <div className="injury-lists-container-v2">
          <InjuryList
            injuries={players.injuries_home || []}
            teamName={players.home_team.name}
            collapsed
          />
          <InjuryList
            injuries={players.injuries_away || []}
            teamName={players.away_team.name}
            collapsed
          />
        </div>
      )}

      {/* Shortlist filter active notice */}
      {showShortlistedOnly && shortlistedCount === 0 && (
        <div className="shortlist-empty-notice-v2">
          No shortlisted players in this fixture
        </div>
      )}

      {/* Player Table */}
      <div className="players-table-container">
        {/* Home Team XI */}
        {(teamFilter === 'both' || teamFilter === 'home') && filterByShortlist(players.predicted_xi_home).length > 0 && (
          <PlayerList
            rows={filterByShortlist(players.predicted_xi_home)}
            window={window}
            statMode={statMode}
            team={players.home_team}
            compareMode={compareMode}
            selectedPlayers={selectedPlayers}
            onPlayerSelect={togglePlayerSelection}
            isPlayerSelected={isPlayerSelected}
            confirmed={players.confirmed}
            onPlayerClick={(player) => setSelectedPlayerForModal({ player, team: players.home_team })}
          />
        )}

        {/* Away Team XI */}
        {(teamFilter === 'both' || teamFilter === 'away') && filterByShortlist(players.predicted_xi_away).length > 0 && (
          <PlayerList
            rows={filterByShortlist(players.predicted_xi_away)}
            window={window}
            statMode={statMode}
            team={players.away_team}
            compareMode={compareMode}
            selectedPlayers={selectedPlayers}
            onPlayerSelect={togglePlayerSelection}
            isPlayerSelected={isPlayerSelected}
            confirmed={players.confirmed}
            onPlayerClick={(player) => setSelectedPlayerForModal({ player, team: players.away_team })}
          />
        )}

        {/* Bench Section - only show when predicted lineups expanded */}
        {showPredictedLineups && (
          <>
            {/* Home Bench */}
            {(teamFilter === 'both' || teamFilter === 'home') && filterByShortlist(players.bench_home).length > 0 && (
              <>
                <div className="players-bench-divider">
                  <span>Bench</span>
                </div>
                <PlayerList
                  rows={filterByShortlist(players.bench_home)}
                  window={window}
                  statMode={statMode}
                  team={players.home_team}
                  compareMode={compareMode}
                  selectedPlayers={selectedPlayers}
                  onPlayerSelect={togglePlayerSelection}
                  isPlayerSelected={isPlayerSelected}
                  confirmed={players.confirmed}
                  onPlayerClick={(player) => setSelectedPlayerForModal({ player, team: players.home_team })}
                  compact
                />
              </>
            )}

            {/* Away Bench */}
            {(teamFilter === 'both' || teamFilter === 'away') && filterByShortlist(players.bench_away).length > 0 && (
              <PlayerList
                rows={filterByShortlist(players.bench_away)}
                window={window}
                statMode={statMode}
                team={players.away_team}
                compareMode={compareMode}
                selectedPlayers={selectedPlayers}
                onPlayerSelect={togglePlayerSelection}
                isPlayerSelected={isPlayerSelected}
                confirmed={players.confirmed}
                onPlayerClick={(player) => setSelectedPlayerForModal({ player, team: players.away_team })}
                compact
              />
            )}
          </>
        )}
      </div>

      {/* Player Profile Modal */}
      {selectedPlayerForModal && (
        <PlayerProfileModal
          playerId={selectedPlayerForModal.player.player_id}
          onClose={() => setSelectedPlayerForModal(null)}
        />
      )}
    </div>
  )
}
